import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Moon, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/lib/supabase";
import ThemeToggle from "@/components/ThemeToggle";
import type { Session } from "@supabase/supabase-js";

function providerDisplayName(session: Session | null): string | null {
  const identity = session?.user?.identities?.[0];
  return identity?.provider ?? null;
}

export default function TopNav() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setReady(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const provider = providerDisplayName(session);
  const email = session?.user?.email ?? null;

  return (
    <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50 dark:border-indigo-500/20 dark:bg-[#0c0a2a]/80">
      <div className="flex w-full items-center justify-between gap-3 px-4 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3 md:gap-8">
          <Link
            to="/home"
            className="flex min-w-0 items-center gap-2.5"
            aria-label="PassiveAgents home"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-yellow-400/20">
              <Moon className="h-5 w-5 text-yellow-300" />
            </div>
            <span className="truncate font-mono text-base font-bold tracking-tight text-slate-900 sm:text-xl dark:text-white">
              PassiveAgents
            </span>
          </Link>
          <Link
            to="/use-cases"
            className="hidden text-sm font-medium text-slate-700 transition-colors hover:text-slate-900 dark:text-indigo-100 dark:hover:text-white md:inline-flex"
          >
            Use Cases
          </Link>
          <Link
            to="/docs?tab=setup"
            className="hidden text-sm font-medium text-slate-700 transition-colors hover:text-slate-900 dark:text-indigo-100 dark:hover:text-white md:inline-flex"
          >
            Docs
          </Link>
          <Link
            to="/pricing"
            className="hidden text-sm font-medium text-slate-700 transition-colors hover:text-slate-900 dark:text-indigo-100 dark:hover:text-white md:inline-flex"
          >
            Pricing
          </Link>
          <a
            href="https://discord.gg/pWWvEjzNTs"
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-1.5 text-sm font-medium text-slate-700 transition-colors hover:text-slate-900 dark:text-indigo-100 dark:hover:text-white md:flex"
          >
            Discord
            <svg
              role="img"
              viewBox="0 0 24 24"
              className="h-4 w-4 fill-current"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
          </a>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <ThemeToggle />
          {ready && (
            <>
              {session ? (
                <>
                  <Button
                    onClick={() => navigate("/app")}
                    variant="ghost"
                    size="sm"
                    className="rounded-full border border-border px-3 text-slate-700 hover:bg-black/5 dark:border-white/10 dark:text-indigo-100 dark:hover:bg-white/5 sm:px-4"
                  >
                    Dashboard
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Open account menu"
                        className="flex items-center gap-2 rounded-full border border-border bg-black/5 px-3 py-1.5 text-slate-700 transition hover:bg-black/10 dark:border-white/10 dark:bg-white/5 dark:text-indigo-100 dark:hover:bg-white/10"
                      >
                        <span className="max-w-24 truncate text-xs sm:max-w-40 sm:text-sm">
                          {email?.trim() || provider || "Signed in"}
                        </span>
                        <ChevronDown className="h-4 w-4 text-slate-500 dark:text-indigo-200/70" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-48 border-border bg-popover text-popover-foreground dark:border-white/10 dark:bg-[#14103a] dark:text-indigo-100"
                    >
                      <DropdownMenuItem
                        onClick={() => navigate("/use-cases")}
                        className="cursor-pointer focus:bg-black/5 focus:text-slate-900 dark:focus:bg-white/10 dark:focus:text-white"
                      >
                        Use Cases
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/docs?tab=setup")}
                        className="cursor-pointer focus:bg-black/5 focus:text-slate-900 dark:focus:bg-white/10 dark:focus:text-white"
                      >
                        Docs
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/billing")}
                        className="cursor-pointer focus:bg-black/5 focus:text-slate-900 dark:focus:bg-white/10 dark:focus:text-white"
                      >
                        Manage Billing
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate("/pricing")}
                        className="cursor-pointer focus:bg-black/5 focus:text-slate-900 dark:focus:bg-white/10 dark:focus:text-white"
                      >
                        Pricing
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border dark:bg-white/10" />
                      <DropdownMenuItem
                        onClick={() => {
                          void supabase.auth
                            .signOut()
                            .then(() => navigate("/home"));
                        }}
                        className="cursor-pointer focus:bg-black/5 focus:text-slate-900 dark:focus:bg-white/10 dark:focus:text-white"
                      >
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => navigate("/login")}
                  variant="ghost"
                  size="sm"
                  className="rounded-full border border-border px-3 text-slate-700 hover:bg-black/5 dark:border-white/10 dark:text-indigo-100 dark:hover:bg-white/5 sm:px-4"
                >
                  Sign in
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
